import {
    Button,
    Drawer,
    DrawerBody,
    DrawerContent,
    DrawerHeader,
    DrawerOverlay,
    Flex,
    Image,
    Input,
    InputGroup,
    InputLeftElement,
    Link,
    Modal,
    ModalContent,
    ModalCloseButton,
    ModalOverlay,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverHeader,
    PopoverTrigger,
    Text,
    useToast,
    IconButton,
} from '@chakra-ui/react';
import React, { KeyboardEvent, MouseEvent, useEffect, useState } from 'react';
import { BrowserView, isBrowser, MobileView } from 'react-device-detect';
import { BsChevronLeft } from 'react-icons/bs';
import { Link as ReactRouterLink } from 'react-router-dom';
import StreamGoldIcon from '../../assets/store/StreamGold.png';
import { AuthenticatedComponent } from '../../auth/keycloak';
import {
    getUserInventory,
    InventoryDef,
    StreamGoldId,
    transferItem,
} from '../../service/inventory';
import { InformationIcon } from '../iconComponents/InformationIcon';

enum DonationState {
    AmountInput,
    Confirmation,
    InvalidAmount,
    SuccessMessage,
}

interface DonationUIProps {
    donateUserID: string;
    isOpen: boolean;
    onClose: () => void;
}

export const DonationUI: AuthenticatedComponent<DonationUIProps> = ({
    donateUserID,
    isOpen,
    onClose,
    keycloak,
}) => {
    const toast = useToast();
    const [inventory, setInventory] = useState<InventoryDef[]>([]);
    const [totalStreamGold, setTotalStreamGold] = useState<number>(0);
    const [pendingDonationGold, setPendingDonationGold] = useState<number>(0);
    const [donatableStreamGold, setDonatableStreamGold] = useState<number>(0);
    const [donationState, setDonationState] = useState(DonationState.AmountInput); // the state is the frame from the figma designs
    const [amount, setAmount] = useState<string>('');

    useEffect(() => {
        if (!isOpen) {
            // when closed, start again from beggining of dialog
            setDonationState(DonationState.AmountInput);
        }
    }, [isOpen]);

    useEffect(() => {
        if (donationState === DonationState.AmountInput) {
            // refresh streamgold state when restarting the donation UI
            getInventory();
        }
    }, [donationState]);

    const getInventory = async () => {
        try {
            const invs = await getUserInventory(
                keycloak.subject,
                'streamsix_currency',
                StreamGoldId,
                keycloak.token,
            );
            console.log('invs', invs);

            let total = 0;
            let pendingDonation = 0;
            let available = 0;
            const currentDate = new Date();
            let filteredInventory: InventoryDef[] = [];

            invs.forEach((inv) => {
                if (inv.inventory_item.item_id === StreamGoldId) {
                    total += inv.quantity;

                    if (!inv.inventory_key.pending) {
                        available += inv.quantity;
                        filteredInventory.push(inv);
                    } else {
                        const releaseDate = new Date(inv.inventory_key.pending.release_date);
                        if (currentDate > releaseDate) {
                            available += inv.quantity;
                            filteredInventory.push(inv);
                        } else {
                            if (inv.inventory_key.pending.source === 'donation') {
                                pendingDonation += inv.quantity;
                            } else {
                                available += inv.quantity;
                                filteredInventory.push(inv);
                            }
                        }
                    }
                }
            });

            setTotalStreamGold(total);
            setPendingDonationGold(pendingDonation);
            setDonatableStreamGold(available);
            setInventory(filteredInventory);
        } catch (err) {
            console.error(err);
        }
    };

    /**
     *
     * @returns true if succesfully donated
     */
    const donateItem = async (): Promise<boolean> => {
        if (inventory.length === 0) {
            return false;
        }

        let toDonate = Number(amount);
        let requestList: Promise<void>[] = [];

        inventory.forEach((inv) => {
            if (toDonate > 0) {
                if (inv.quantity >= toDonate) {
                    //full amount reached
                    const take = toDonate;
                    requestList.push(transferItem(inv, take, donateUserID, keycloak.token));
                    toDonate = 0;
                } else {
                    //more needed
                    requestList.push(transferItem(inv, inv.quantity, donateUserID, keycloak.token));
                    toDonate -= inv.quantity;
                }
            }
        });

        try {
            await Promise.all(requestList);
            await getInventory();
            return true;
        } catch (err) {
            console.error(err);
            toast({
                title: 'Failed to transfer',
                description: (err as Error).message,
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
            return false;
        }
    };

    const submitAmount = (): void => {
        if (!amount || !inventory) {
            return;
        }

        const numAmount = Number(amount);

        if (numAmount > 0 && numAmount <= donatableStreamGold) {
            setDonationState(DonationState.Confirmation);
        } else {
            setDonationState(DonationState.InvalidAmount);
        }
    };

    /**
     * The core logic
     */
    const nextStage = async (
        e: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLInputElement>,
    ): Promise<void> => {
        const buttonId = e.currentTarget.id;
        switch (donationState) {
            case DonationState.AmountInput:
                if (buttonId === 'stage-1-continue' || ('key' in e && e.key === 'Enter')) {
                    submitAmount();
                }
                return;
            case DonationState.Confirmation:
                switch (buttonId) {
                    case 'stage-2-cancel':
                        onClose();
                        return;
                    case 'stage-2-confirm':
                        (await donateItem()) && setDonationState(DonationState.SuccessMessage);
                        return;
                    default:
                        throw new Error('Invalid option ' + buttonId);
                }
            case DonationState.InvalidAmount:
                if (buttonId === 'stage-3-cancel') {
                    onClose();
                }
                return;
            case DonationState.SuccessMessage:
                if (buttonId === 'stage-4-close') {
                    onClose();
                }
                break;
        }
    };

    const prevStage = (): void => {
        if (donationState === DonationState.SuccessMessage) {
            setDonationState(DonationState.Confirmation);
        } else if (donationState === DonationState.InvalidAmount) {
            setDonationState(DonationState.AmountInput);
        } else {
            setDonationState(Math.max(0, donationState - 1));
        }
    };

    return (
        <>
            <MobileView>
                <Drawer
                    placement="bottom"
                    isOpen={isOpen}
                    onClose={() => {
                        onClose();
                        setDonationState(DonationState.AmountInput);
                    }}
                >
                    <DrawerOverlay />
                    <DrawerContent className={'donate-content'}>
                        {donationState !== DonationState.SuccessMessage && (
                            <DrawerHeader>
                                <Flex className={'donate-header'}>
                                    {/* TODO: Should this go back or exit in general and specifically on the last stage */}
                                    <BsChevronLeft
                                        onClick={() =>
                                            donationState === DonationState.AmountInput
                                                ? onClose()
                                                : prevStage()
                                        }
                                    />
                                    <Text className={'donate-header-title'}>Donation</Text>
                                </Flex>
                            </DrawerHeader>
                        )}
                        <DrawerBody className={'donate-body'}>
                            {stages(
                                donationState,
                                amount,
                                setAmount,
                                nextStage,
                                inventory,
                                totalStreamGold,
                                pendingDonationGold,
                                donatableStreamGold,
                                onClose,
                            )}
                        </DrawerBody>
                    </DrawerContent>
                </Drawer>
            </MobileView>
            <BrowserView>
                <Modal isOpen={isOpen} onClose={onClose} isCentered>
                    <ModalOverlay />
                    <ModalContent className={'donate-content'}>
                        <Flex className={'donate-body browser'}>
                            <ModalCloseButton />
                            {stages(
                                donationState,
                                amount,
                                setAmount,
                                nextStage,
                                inventory,
                                totalStreamGold,
                                pendingDonationGold,
                                donatableStreamGold,
                                () => {
                                    onClose();
                                    setDonationState(DonationState.AmountInput);
                                },
                            )}
                        </Flex>
                    </ModalContent>
                </Modal>
            </BrowserView>
        </>
    );
};

const stages = (
    donationState: DonationState,
    amount: string,
    setAmount: React.Dispatch<React.SetStateAction<string>>,
    nextStage: (
        e: KeyboardEvent<HTMLInputElement> | MouseEvent<HTMLButtonElement>,
    ) => Promise<void>,
    inventory: InventoryDef[],
    totalStreamGold: number,
    pendingStreamGold: number,
    donatableStreamgold: number,
    onClose: () => void,
) => {
    return (
        <>
            {donationState === DonationState.AmountInput && (
                <Stage1
                    amount={amount}
                    setAmount={setAmount}
                    nextStage={nextStage}
                    streamgoldInventory={inventory}
                    totalStreamGold={totalStreamGold}
                    pendingStreamGold={pendingStreamGold}
                    donatableStreamgold={donatableStreamgold}
                />
            )}

            {donationState === DonationState.Confirmation && (
                <Stage2 amount={amount} nextStage={nextStage} />
            )}

            {donationState === DonationState.InvalidAmount && <Stage3 onClose={onClose} />}

            {donationState === DonationState.SuccessMessage && (
                <Stage4
                    nextStage={nextStage}
                    streamgoldInventory={inventory}
                    totalStreamGold={totalStreamGold}
                    pendingStreamGold={pendingStreamGold}
                    donatableStreamgold={donatableStreamgold}
                />
            )}
        </>
    );
};
interface Stage1Props {
    amount: string;
    setAmount: React.Dispatch<React.SetStateAction<string>>;
    nextStage: (
        e: KeyboardEvent<HTMLInputElement> | MouseEvent<HTMLButtonElement>,
    ) => Promise<void>;
    streamgoldInventory: InventoryDef[];
    totalStreamGold: number;
    pendingStreamGold: number;
    donatableStreamgold: number;
}

const Stage1: React.FC<Stage1Props> = ({
    amount,
    setAmount,
    nextStage,
    streamgoldInventory,
    totalStreamGold,
    pendingStreamGold,
    donatableStreamgold,
}) => {
    return (
        <div className={'stage-1'}>
            {streamgoldInventory && (
                <Flex direction={'column'}>
                    <Flex className={'top section'}>
                        <Text className={'title'}>Total Balance:</Text>
                        <StreamGoldAmount amount={totalStreamGold.toString()} />
                    </Flex>
                    <Flex className={'top section'}>
                        <Flex>
                            <Text className={'title'}>Pending from Donations:</Text>
                            <Popover>
                                <PopoverTrigger>
                                    <IconButton
                                        variant={'unstyled'}
                                        icon={<InformationIcon />}
                                        aria-label={'available-balance'}
                                    />
                                </PopoverTrigger>
                                <PopoverContent>
                                    <PopoverHeader>Pending Donation balance:</PopoverHeader>
                                    <PopoverBody>
                                        STREAMGOLD received as a donation from another user will be
                                        in a pending state for 30 days, once cleared it will become
                                        available to donate.
                                    </PopoverBody>
                                </PopoverContent>
                            </Popover>
                        </Flex>
                        <StreamGoldAmount amount={pendingStreamGold.toString()} />
                    </Flex>
                    <Flex className={'top section'}>
                        <Text className={'title'}>Available for Donation:</Text>
                        <StreamGoldAmount amount={donatableStreamgold.toString()} />
                    </Flex>
                </Flex>
            )}

            <Flex className={'middle section'}>
                <Text>STREAMGOLD to Donate:</Text>
                {/* TODO: Inavlid amount */}
                <InputGroup className={'amount-input'}>
                    <InputLeftElement className={'streamgold-logo'}>
                        <Image src={StreamGoldIcon} />
                    </InputLeftElement>
                    <Input
                        focusBorderColor={'none'}
                        type={'number'}
                        onChange={(e) => setAmount(e.target.value)}
                        // Prevent inputting non-digit characters that are allowed when `type=number`
                        onKeyDown={(e) =>
                            e.key === 'Enter'
                                ? nextStage(e)
                                : ['e', 'E', '+', '-', '.'].includes(e.key) && e.preventDefault()
                        }
                        // Prevent pasting non digit numbers
                        onPaste={(e) =>
                            e.clipboardData.getData('text/plain').match(/[^\d]/) &&
                            e.preventDefault()
                        }
                        value={amount}
                        className={'amount-number'}
                    />
                </InputGroup>
            </Flex>

            <Flex className={'bottom section'}>
                <Button onClick={nextStage} id={'stage-1-continue'}>
                    Continue
                </Button>
            </Flex>
        </div>
    );
};

const StreamGoldAmount: React.FC<{ amount: string | undefined }> = ({ amount }) => {
    return (
        <Flex>
            <Image src={StreamGoldIcon} className={'streamgold-amount-icon'} />
            <Text className={'streamgold-amount-text'}>{amount} STREAMGOLD</Text>
        </Flex>
    );
};

interface Stage2Props {
    amount: string | undefined;
    nextStage: (e: MouseEvent<HTMLButtonElement>) => Promise<void>;
}

const Stage2: React.FC<Stage2Props> = ({ amount, nextStage }) => {
    return (
        <div className={'stage-2'}>
            <Flex className={'top section'}>
                <Text>You are donating: </Text>
                <StreamGoldAmount amount={amount} />
            </Flex>

            <Flex className={'bottom section'}>
                <Button className={'cancel-button'} onClick={nextStage} id={'stage-2-cancel'}>
                    Cancel
                </Button>
                <Button className={'confirm-button'} onClick={nextStage} id={'stage-2-confirm'}>
                    Confirm
                </Button>
            </Flex>
        </div>
    );
};

interface Stage3Props {
    onClose: () => void;
}

const Stage3: React.FC<Stage3Props> = ({ onClose }) => {
    return (
        <div className={'stage-3'}>
            <Flex className={'top section'}>
                <Text>Insufficient funds, please purchase Stream Gold in the store</Text>
            </Flex>

            <Flex className={'bottom section'}>
                <Button className={'cancel-button'} onClick={onClose} id={'stage-3-cancel'}>
                    Cancel
                </Button>
                <Link className={'store-link'} as={ReactRouterLink} to={'/store'}>
                    <Button>Store</Button>
                </Link>
            </Flex>
        </div>
    );
};

interface Stage4Props {
    nextStage: (e: MouseEvent<HTMLButtonElement>) => Promise<void>;
    streamgoldInventory: InventoryDef[];
    totalStreamGold: number;
    pendingStreamGold: number;
    donatableStreamgold: number;
}

const Stage4: React.FC<Stage4Props> = ({
    nextStage,
    streamgoldInventory,
    totalStreamGold,
    pendingStreamGold,
    donatableStreamgold,
}) => {
    return (
        <div className={'stage-4'}>
            {isBrowser && streamgoldInventory && (
                <Flex direction={'column'}>
                    <Flex className={'top section'}>
                        <Text className={'title'}>Total Balance:</Text>
                        <StreamGoldAmount amount={totalStreamGold.toString()} />
                    </Flex>
                    <Flex className={'top section'}>
                        <Text className={'title'}>Pending from Donations:</Text>
                        <StreamGoldAmount amount={pendingStreamGold.toString()} />
                    </Flex>
                    <Flex className={'top section'}>
                        <Text className={'title'}>Available for Donation:</Text>
                        <StreamGoldAmount amount={donatableStreamgold.toString()} />
                    </Flex>
                </Flex>
            )}
            <Flex className={'middle section'}>
                <Text>Thank you for your contribution!</Text>
            </Flex>
            <Flex className="bottom section">
                <Button id={'stage-4-close'} onClick={nextStage}>
                    Close
                </Button>
            </Flex>
        </div>
    );
};
